<template>
  <NavigationBar />
  <div class="warranty-container">
    <!-- Header Section with Integrated Back Button -->
    <div class="header-section mt-14">                                                         </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-animation">
        <div class="loading-dots">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        <p>Loading warranty doc uments...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="error-content">
        <div class="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3>Unable to Load Warranties</h3>
        <p>{{ error }}</p>
        <button @click="created()" class="retry-btn">Try Again</button>
      </div>
    </div>

    <!-- Warranties Grid -->
    <div v-if="!loading && warranties.length > 0" class="modern-grid">
      <div v-for="kitName in predefinedKitNames" :key="kitName" class="kit-modern-card">
        <div
          class="kit-modern-header"
          :class="{
            'has-warranties': groupedWarranties[kitName] && groupedWarranties[kitName].length > 0,
            'no-warranties': !groupedWarranties[kitName] || groupedWarranties[kitName].length === 0,
            'expanded': openKitName === kitName
          }"
          @click="toggleKit(kitName)"
        >
          <div class="kit-modern-info">
            <div class="kit-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div class="kit-text-content">
              <h3 class="kit-modern-name">{{ kitName }}</h3>
              <div class="kit-status">
                <span class="status-badge" :class="{
                  'available': groupedWarranties[kitName] && groupedWarranties[kitName].length > 0,
                  'unavailable': !groupedWarranties[kitName] || groupedWarranties[kitName].length === 0
                }">
                  {{ groupedWarranties[kitName] && groupedWarranties[kitName].length > 0 ? `${groupedWarranties[kitName].length} warranty files` : 'No warranties' }}
                </span>
              </div>
            </div>
          </div>
          <div class="kit-modern-actions">
            <svg
              v-if="groupedWarranties[kitName] && groupedWarranties[kitName].length > 0"
              xmlns="http://www.w3.org/2000/svg"
              class="modern-chevron"
              :class="{ 'rotated': openKitName === kitName }"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <!-- Warranty Files -->
        <div v-show="openKitName === kitName" class="kit-modern-content">
          <div class="warranty-modern-list">
            <div v-for="warranty in groupedWarranties[kitName]" :key="warranty.id" class="warranty-modern-item">
              <div class="warranty-modern-info">
                <div class="file-type-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div class="warranty-modern-details">
                  <p class="warranty-modern-title">Warranty Certificate</p>
                  <p class="warranty-modern-date">
                    <svg xmlns="http://www.w3.org/2000/svg" class="calendar-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Generated {{ warranty.created_at_str }}
                  </p>
                </div>
              </div>
              <a :href="warranty.file" target="_blank" class="modern-download-btn">
                <span>Download</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="download-modern-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && warranties.length === 0" class="empty-modern-state">
      <div class="empty-modern-content">
        <div class="empty-modern-icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3>No Warranty Documents Available</h3>
        <p>Warranty certificates for this order haven't been generated yet. Please check back later.</p>
        <div class="empty-actions">
          <button @click="$router.back()" class="empty-back-btn">Back to Orders</button>
          <button @click="created()" class="empty-refresh-btn">Refresh</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import NavigationBar from "@/components/NavigationBar.vue";

export default {
  name: 'ViewOrderWarranties',
  props: ['sr'], // we have sent this value via url so from there we will get this
  components: {
    NavigationBar
  },
  data() {
    return {
      warranties: [],
      loading: true,
      error: null,
      openKitName: null,
      predefinedKitNames: [
        '2P×3, 10°, 6.5 FT',
        '2P×3, 10°, 8.2 FT',
        '2P×5, 10°, 6.5 FT',
        '2P×5, 10°, 8.2 FT',
        '2P×3, 15°, 6.5 FT',
        '2P×3, 15°, 8.2 FT',
        '2P×5, 15°, 6.5 FT',
        '2P×5, 15°, 8.2 FT',
      ]
    };
  },
  computed: {
    groupedWarranties() {
      const groups = {};
      this.predefinedKitNames.forEach(name => {
        groups[name] = [];
      });
      this.warranties.forEach(warranty => {
        if (groups[warranty.kit_name]) {
          groups[warranty.kit_name].push(warranty);
        }
      });
      return groups;
    }
  },
  methods: {
    toggleKit(kitName) {
      if (!this.groupedWarranties[kitName] || this.groupedWarranties[kitName].length === 0) {
        return;
      }

      if (this.openKitName === kitName) {
        this.openKitName = null;
      } else {
        this.openKitName = kitName;
      }
    }
  },
  async created() {
    try {
      this.loading = true;
      this.error = null;
      const response = await axios.get(`/created-warranties/?sr=${this.sr}`);
      this.warranties = response.data;
    } catch (err) {
      console.error("Error fetching warranties:", err);
      this.error = "Failed to load warranties. Please check your connection and try again.";
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.warranty-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  position: relative;
}

/* Header Section */
.header-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  align-items: start;
}

.header-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
}

.header-top {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(79, 70, 229, 0.1);
  border: 1px solid rgba(79, 70, 229, 0.2);
  border-radius: 12px;
  color: #4f46e5;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
}

.back-btn:hover {
  background: rgba(79, 70, 229, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(79, 70, 229, 0.2);
}

.back-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.order-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.5rem;
}

.badge-text {
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.sr-number {
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b, #374151);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.subtitle {
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.6;
}

.header-stats {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #10b981, #34d399);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon svg {
  width: 1.5rem;
  height: 1.5rem;
  color: white;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

/* Modern Grid */
.modern-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.kit-modern-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.kit-modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.kit-modern-header {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.kit-modern-header.has-warranties:hover {
  background: rgba(79, 70, 229, 0.05);
}

.kit-modern-header.expanded {
  background: rgba(79, 70, 229, 0.08);
  border-bottom: 1px solid rgba(79, 70, 229, 0.1);
}

.kit-modern-header.no-warranties {
  cursor: not-allowed;
  opacity: 0.7;
}

.kit-modern-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.kit-icon {
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kit-icon svg {
  width: 1.5rem;
  height: 1.5rem;
  color: white;
}

.kit-text-content {
  flex: 1;
}

.kit-modern-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.kit-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.status-badge.available {
  background: rgba(16, 185, 129, 0.1);
  color: #047857;
}

.status-badge.unavailable {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.modern-chevron {
  width: 1.25rem;
  height: 1.25rem;
  color: #64748b;
  transition: all 0.3s ease;
}

.modern-chevron.rotated {
  transform: rotate(180deg);
  color: #4f46e5;
}

/* Warranty Content */
.kit-modern-content {
  padding: 0;
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.kit-modern-header.expanded + .kit-modern-content {
  padding: 1.5rem;
  max-height: 500px;
}

.warranty-modern-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.warranty-modern-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.2s ease;
}

.warranty-modern-item:hover {
  background: white;
  border-color: #e2e8f0;
  transform: translateX(5px);
}

.warranty-modern-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.file-type-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-type-icon svg {
  width: 1.25rem;
  height: 1.25rem;
  color: white;
}

.warranty-modern-details {
  flex: 1;
}

.warranty-modern-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.warranty-modern-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.calendar-icon {
  width: 1rem;
  height: 1rem;
}

.modern-download-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  border: none;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: pointer;
}

.modern-download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(79, 70, 229, 0.4);
}

.download-modern-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-animation {
  text-align: center;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.dot {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-animation p {
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
}

/* Error State */
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.error-icon {
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.error-icon svg {
  width: 2rem;
  height: 2rem;
  color: white;
}

.error-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.error-content p {
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.retry-btn {
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(79, 70, 229, 0.4);
}

/* Empty State */
.empty-modern-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.empty-modern-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.empty-modern-icon {
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #6b7280, #9ca3af);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.empty-modern-icon svg {
  width: 2rem;
  height: 2rem;
  color: white;
}

.empty-modern-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.empty-modern-content p {
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.empty-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.empty-back-btn, .empty-refresh-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.empty-back-btn {
  background: #4f46e5;
  color: white;
}

.empty-refresh-btn {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
}

.empty-back-btn:hover, .empty-refresh-btn:hover {
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-section {
    grid-template-columns: 1fr;
  }

  .modern-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .warranty-container {
    padding: 1rem;
  }

  .header-content {
    padding: 1.5rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .warranty-modern-item {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .modern-download-btn {
    justify-content: center;
  }

  .empty-actions {
    flex-direction: column;
  }

  .header-top {
    justify-content: center;
  }

  .back-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>